/**
 * Small shared helpers for the bot modules: ammo checks, enemy filtering,
 * candidate positions and enemy line-of-sight counting.
 */

import { seatWeapons } from '../../core/seats';
import type { PlayerState, PlanetState, WeaponKey, Vec3, GameState } from '../../types';
import { hasLineOfSight, losEyePoint } from '../../core/los';
import { vecNormalize, vecDot, vecCross, vecScale, vecAdd } from '../../math';
import { WEAPON_KEYS, HEAL_MOVEMENT_COST } from '../../constants';
import { getPlayerWorldPosition } from '../../core/gameState';
import { wasmCountEnemyLOS } from '../wasmPhysics';
import { dcos, dsin } from '../../core/dmath';

export function getPlayerWorldPos(player: PlayerState, planets: PlanetState[]): Vec3 {
  return getPlayerWorldPosition(player, planets);
}

/** Check if player has ammo for a weapon */
export function hasAmmo(player: PlayerState, key: WeaponKey): boolean {
  const ammo = player.loadout.ammo[key];
  // null = unlimited; an ABSENT count is nothing (it used to read as unlimited, which handed every bot whose
  // loadout did not list a weapon — the harness's, for one — free specials it never bought)
  return ammo === null || (ammo !== undefined && ammo > 0);
}

/** Get list of weapons the player has ammo for */
export function getAvailableWeapons(player: PlayerState, state?: Pick<GameState, 'structures'>): WeaponKey[] {
  // S6: a seated unit fires the seat's weapons from the gun's own ammunition, as well as its own
  const seat = state && player.mount ? seatWeapons(player, state) : [];
  return WEAPON_KEYS.filter(key => hasAmmo(player, key) || seat.includes(key));
}

/** Check if bot has any firing ammo at all */
export function hasAnyAmmo(player: PlayerState): boolean {
  return getAvailableWeapons(player).length > 0;
}

/** Check if player has any heal items and can afford the movement cost */
export function canBotHeal(player: PlayerState): boolean {
  if (player.health >= 100) return false;
  if (player.movementRemaining < HEAL_MOVEMENT_COST) return false;
  const items = player.loadout.items;
  if (!items) return false;
  return ((items.healSmall ?? 0) > 0) || ((items.healLarge ?? 0) > 0);
}

/**
 * Generate N evenly-spaced candidate directions around a point on a planet surface
 * using tangent-plane offsets.
 */
export function generateCandidateDirections(currentDir: Vec3, count: number, offset = 0.6): Vec3[] {
  const dir = vecNormalize(currentDir);
  let ref: Vec3 = { x: 1, y: 0, z: 0 };
  if (Math.abs(vecDot(dir, ref)) > 0.9) ref = { x: 0, y: 1, z: 0 };
  const tx = vecNormalize(vecCross(dir, ref));
  const ty = vecNormalize(vecCross(dir, tx));

  const result: Vec3[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    result.push(vecNormalize(vecAdd(
      dir,
      vecAdd(
        vecScale(tx, dcos(angle) * offset),
        vecScale(ty, dsin(angle) * offset)
      )
    )));
  }
  return result;
}

/**
 * Count how many alive enemies have unblocked LOS to a candidate world position.
 * Excludes the candidate's own planet and each enemy's planet from blocking checks
 * (matching existing LOS convention in fog.ts getVisibleEnemies).
 */
export function countEnemyLOS(
  candidateWorldPos: Vec3,
  candidatePlanetIndex: number,
  enemies: PlayerState[],
  planets: PlanetState[],
  useWasm?: boolean,
): number {
  if (useWasm) {
    const result = wasmCountEnemyLOS(candidateWorldPos, candidatePlanetIndex, enemies, planets);
    if (result !== null) return result;
  }
  // `candidateWorldPos` is an eye point (see core/los.ts); same rule as fog spotting.
  void candidatePlanetIndex;
  let count = 0;
  for (const enemy of enemies) {
    if (!enemy.alive) continue;
    if (hasLineOfSight(losEyePoint(enemy, planets), candidateWorldPos, planets)) count++;
  }
  return count;
}

/** Get alive enemies (excludes teammates) */
export function getAliveEnemies(player: PlayerState, players: PlayerState[]): PlayerState[] {
  return players.filter(p => p.id !== player.id && p.alive && p.teamIndex !== player.teamIndex);
}
